import { ensureLoaded } from './domain-blocklist-cache.js';
import { openExternalUrl } from './open-external-url.js';

const PREVIEW_MIN_HEIGHT = 120;
const channelCleanups = new WeakMap();
const previewConfigGetters = new WeakMap();
const WIDGET_HOST_PLACEHOLDER = '__FOT_WIDGET_HOST_DOMAIN__';
const DEFAULT_WIDGET_HOST = 'http://localhost:19883';

export function getWidgetHostDomain() {
  const raw = window.FOT_WIDGET_HOST_DOMAIN;
  if (!raw || raw === WIDGET_HOST_PLACEHOLDER) {
    return DEFAULT_WIDGET_HOST;
  }
  try {
    const url = new URL(raw);
    return url.origin;
  } catch {
    return DEFAULT_WIDGET_HOST;
  }
}

export function cleanupWidgetIframeChannel(iframe) {
  const cleanup = channelCleanups.get(iframe);
  if (cleanup) cleanup();
  channelCleanups.delete(iframe);
}

export function setWidgetPreviewConfigGetter(iframe, getter) {
  if (typeof getter === 'function') {
    previewConfigGetters.set(iframe, getter);
  } else {
    previewConfigGetters.delete(iframe);
  }
}

export function setupWidgetIframeChannel(iframe, { widgetId = '', instanceId = '', ownerUserId = '', onReady, onResize, getInstanceConfig } = {}) {
  ensureLoaded();
  cleanupWidgetIframeChannel(iframe);
  const channel = new MessageChannel();
  const port = channel.port1;

  const onMessage = (event) => {
    const { type, requestId, height, url } = event.data || {};
    if (type === 'FOT.READY' || type === 'WIDGET_READY') {
      onReady?.();
    }
    if ((type === 'FOT.RESIZE' || type === 'WIDGET_RESIZE') && height) {
      const h = Math.max(PREVIEW_MIN_HEIGHT, Number(height));
      onResize?.(h);
      iframe.style.height = `${h}px`;
    }
    if (type === 'FOT.OPEN_EXTERNAL') {
      openExternalUrl(url);
    }
    if (type === 'FOT.GET_INSTANCE_CONFIG' && requestId) {
      const resolveConfig = () => {
        const getter = typeof getInstanceConfig === 'function'
          ? getInstanceConfig
          : previewConfigGetters.get(iframe);
        if (typeof getter === 'function') {
          return Promise.resolve(getter()).then((config) => config || {});
        }
        return Promise.resolve({});
      };
      resolveConfig()
        .then((config) => {
          port.postMessage({ type: 'FOT.INSTANCE_CONFIG', requestId, config });
        })
        .catch((err) => {
          window.logger?.warn('Widget preview config failed:', err);
          port.postMessage({
            type: 'FOT.ERROR',
            requestId,
            code: 'UPSTREAM_ERROR',
            message: 'Config request failed'
          });
        });
    }
  };

  port.addEventListener('message', onMessage);
  port.start();
  channelCleanups.set(iframe, () => {
    port.removeEventListener('message', onMessage);
    port.close();
  });

  const widgetOrigin = new URL(getWidgetHostDomain()).origin;
  iframe.contentWindow?.postMessage({
    type: 'WIDGET_CHANNEL_INIT',
    widgetId: String(widgetId),
    instanceId: instanceId ? String(instanceId) : '',
    ownerUserId: ownerUserId ? String(ownerUserId) : ''
  }, widgetOrigin, [channel.port2]);
}

export function bindWidgetHostPreviewIframe(iframe, { onReady, onError, getInstanceConfig } = {}) {
  if (typeof getInstanceConfig === 'function') {
    setWidgetPreviewConfigGetter(iframe, getInstanceConfig);
  }
  if (iframe.dataset.widgetHostPreviewBound) {
    return;
  }
  iframe.dataset.widgetHostPreviewBound = '1';
  iframe.style.height = `${PREVIEW_MIN_HEIGHT}px`;

  iframe.addEventListener('load', () => {
    try {
      setupWidgetIframeChannel(iframe, {
        widgetId: iframe.dataset.widgetId || '',
        onReady,
        onResize: (h) => { iframe.style.height = `${h}px`; }
      });
    } catch (err) {
      window.logger?.warn('Widget preview channel failed:', err);
      onError?.(err);
    }
  });

  iframe.addEventListener('error', () => {
    onError?.(new Error('Widget preview iframe failed to load'));
  });
}

export function loadWidgetHostPreview(iframe, url, { widgetId = '', onReady, onError, getInstanceConfig } = {}) {
  iframe.dataset.widgetId = String(widgetId);
  bindWidgetHostPreviewIframe(iframe, { onReady, onError, getInstanceConfig });
  iframe.removeAttribute('srcdoc');
  iframe.src = url;
}

export function submitDraftPreviewToIframe(iframe, token, fields, formHost, { onReady, onError, getInstanceConfig } = {}) {
  const host = getWidgetHostDomain();
  const container = formHost || iframe.closest('widget-management') || document.body;

  if (!iframe.name) {
    iframe.name = iframe.dataset.previewName || `widget-preview-${Date.now()}`;
  }

  iframe.dataset.widgetId = String(fields.widgetId || '');
  bindWidgetHostPreviewIframe(iframe, { onReady, onError, getInstanceConfig });

  let form = container.querySelector('[data-widget-draft-preview-form]');
  if (!form) {
    form = document.createElement('form');
    form.setAttribute('data-widget-draft-preview-form', '');
    form.method = 'POST';
    form.style.display = 'none';
    container.appendChild(form);
  }

  form.action = `${host}/render/preview/draft?token=${encodeURIComponent(token)}`;
  form.target = iframe.name;
  form.innerHTML = '';

  const entries = {
    name: fields.name || 'Widget preview',
    htmlContent: fields.htmlContent || '',
    cssContent: fields.cssContent || '',
    jsContent: fields.jsContent || ''
  };
  if (fields.widgetId) {
    entries.widgetId = String(fields.widgetId);
  }

  for (const [key, val] of Object.entries(entries)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = val;
    form.appendChild(input);
  }

  iframe.removeAttribute('srcdoc');
  form.submit();
}

export function widgetHostUnavailableMessage() {
  return `Widget preview unavailable — is widget-host running at ${getWidgetHostDomain()}?`;
}
